import Phaser from 'phaser';
import { INTRO_CONFIG } from '../world.config.js';
import { hasVisited } from '../systems/DialogueSystem.js';

// Trailer mode — visiting `?trailer=1` skips all intro cinematics + UI
// chrome so the page boots straight into a scripted camera sweep through
// the studio. Used for screen-recording promo MP4s.
function isTrailerMode() {
  return new URLSearchParams(window.location.search).get('trailer') === '1';
}

const NAME_FADE_MS = 700;
const TAGLINE_FADE_MS = 600;
const BOOT_LINE_DELAY_MS = 280;
const BOOT_LINE_TYPE_MS = 28;
const HOLD_AFTER_BOOT_MS = 600;
const FADE_TO_WHITE_MS = 350;

// Cinematic title sequence. Skipped to a brief beat for repeat visitors.
export class IntroScene extends Phaser.Scene {
  constructor() {
    super('IntroScene');
  }

  create() {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#0a0806');
    this.cameras.main.fadeIn(300, 0, 0, 0);

    // Trailer mode — skip title cinematic entirely so the recording starts
    // straight into the studio sweep. URL: `?trailer=1`.
    if (isTrailerMode()) {
      this.scene.start('HouseScene');
      return;
    }

    // Repeat visitors get a short flash, not the full sequence
    if (hasVisited()) {
      this._renderShort(width, height);
      return;
    }

    this._renderFull(width, height);
  }

  _renderShort(width, height) {
    const text = this.add.text(width / 2, height / 2, 'welcome back.', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#f4ead5',
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 400,
      yoyo: true,
      hold: 350,
      onComplete: () => this._exitToHouse(),
    });
  }

  _renderFull(width, height) {
    const cx = width / 2;
    const cy = height / 2;

    const name = this.add.text(cx, cy - 28, INTRO_CONFIG.name.toUpperCase(), {
      fontFamily: 'monospace',
      fontSize: '28px',
      color: '#f4ead5',
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0);
    name.setLetterSpacing?.(4);

    const tagline = this.add.text(cx, cy + 8, INTRO_CONFIG.tagline, {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#f6b93b',
    }).setOrigin(0.5).setAlpha(0);

    const bootBox = this.add.text(cx, cy + 60, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#9bd28a',
      align: 'left',
    }).setOrigin(0.5, 0).setAlpha(0);

    // Animation chain
    this.tweens.add({
      targets: name,
      alpha: 1,
      duration: NAME_FADE_MS,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: tagline,
          alpha: 1,
          duration: TAGLINE_FADE_MS,
          ease: 'Sine.easeOut',
          onComplete: () => {
            bootBox.setAlpha(1);
            this._typeBootLines(bootBox, INTRO_CONFIG.bootLines, 0, () => {
              this.time.delayedCall(HOLD_AFTER_BOOT_MS, () => this._exitToHouse());
            });
          },
        });
      },
    });

    // Skip on any key
    const skipHandler = () => this._exitToHouse();
    this.input.keyboard.once('keydown', skipHandler);
    this.input.once('pointerdown', skipHandler);
  }

  _typeBootLines(textObj, lines, lineIdx, done) {
    if (lineIdx >= lines.length) {
      done();
      return;
    }
    const fullLine = `> ${lines[lineIdx]}`;
    let charIdx = 0;
    const acc = textObj.text;
    const tick = () => {
      if (charIdx > fullLine.length) {
        textObj.setText(acc + fullLine + '\n');
        this.time.delayedCall(BOOT_LINE_DELAY_MS, () => {
          this._typeBootLines(textObj, lines, lineIdx + 1, done);
        });
        return;
      }
      textObj.setText(acc + fullLine.slice(0, charIdx));
      charIdx += 1;
      this.time.delayedCall(BOOT_LINE_TYPE_MS, tick);
    };
    tick();
  }

  _exitToHouse() {
    if (this._exiting) return;
    this._exiting = true;
    this.cameras.main.fadeOut(FADE_TO_WHITE_MS, 244, 234, 213);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('HouseScene');
    });
  }
}
